import styled, { css } from 'styled-components'
import colorsOptions from '../../components/stories/fundamentals/colors-styles/constants'
import { HeaderContainer as TableHeaderContainer } from '../../components/stories/organisms/table/components/header/styled-components'
import { ToolbarContainer } from '../../components/stories/organisms/table/components/toolbar/styled-components'
import {
  BodyTrDnD,
  TableDndFrame,
  TableStyled,
  TableWrapper
} from '../../components/stories/organisms/table/styled-components'
import {
  textStyleSizeOptions,
  textStyleSizeStyleOptions,
  textStyleWeightOptions,
  textStyleWeightStyleOptions
} from '../../components/stories/fundamentals/text-styles/constants'

// main
export const PlanMenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2.5rem 1rem;
  min-height: 100%;
`

export const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 90vh;
  width: 100%;
`

// header
export const PlanMenuHeaderContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding-bottom: 2rem;
`

export const PlanMenuHeaderTitle = styled.div`
  flex: 1;
  min-width: 0;
  color: ${colorsOptions.greys.black};
  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-700']]}
  ${textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-XXXL']]}

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

export const PlanMenuHeaderTitleSpan = styled.span`
  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-500']]}
`

export const PlanMenuHeaderActions = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 0.5rem;
`

export const PlanMenuSelectionView = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`

export const ButtonChildrenContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`

// Plan menu list

export const PlanMenuTableScrollContainer = styled.div`
  overflow-x: auto;
  /* the card only ever scrolls sideways, but declaring overflow-x alone promotes
     overflow-y to auto as well. That vertical scroll is never used by the layout
     — only by the dragged row's transform, which extends the scroll area and
     feeds dnd-kit's auto-scroll into growing it further */
  overflow-y: hidden;
`

export const PlanMenuTableContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;

  ${ToolbarContainer} {
    padding: 0 0.5rem 0 0;
    /* the toolbar defaults to flex: 1, which would make it share the container's
       free height with the table card */
    flex: 0 0 auto;
  }

  ${({ $isEmpty }) =>
    $isEmpty &&
    css`
      flex: 1;
    `}
`

export const PlanMenuTableWrapper = styled.div`
  width: 100%;
  background-color: ${colorsOptions.greys.white};
  border: 1px solid ${colorsOptions.greys['grey-20']};
  border-radius: 16px;
  overflow: visible;

  ${TableWrapper} {
    gap: 0;
  }

  ${TableStyled} {
    border: 0;
    border-radius: 0;
  }

  /* the header row is the top of the card now that the toolbar sits outside it,
     so it follows the card radius instead of the table's own 8px */
  ${TableHeaderContainer} {
    &:first-child {
      border-top-left-radius: 16px;
    }

    &:last-child {
      border-top-right-radius: 16px;
    }
  }

  /* only the empty state fills the viewport: the placeholder stretches to the
     bottom of the card instead of leaving a blank area below its fixed height.
     With rows to render, the card keeps its natural height */
  ${({ $isEmpty }) =>
    $isEmpty &&
    css`
      display: flex;
      flex: 1;
      flex-direction: column;

      ${TableWrapper}, ${PlanMenuTableScrollContainer}, ${TableDndFrame} {
        display: flex;
        flex: 1;
        flex-direction: column;
      }

      ${TableStyled} {
        flex: 1;
      }
    `}
`

/* design asks for the whole row to be draggable, not just the handle cell, so
   the grab affordance sits on the <tr>. `user-select: none` keeps the pointer
   from selecting the row text while it travels the sensor activation distance */
export const PlanMenuDraggableRow = styled(BodyTrDnD)`
  ${({ $canDrag, isDragging }) =>
    $canDrag &&
    css`
      cursor: ${isDragging ? 'grabbing' : 'grab'};
      user-select: none;
    `}
`

export const PlanMenuTableStatusContent = styled.div`
  min-height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colorsOptions.greys['grey-60']};
  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-500']]}
  ${textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-R']]}
`

export const PlanMenuTablePaginationContainer = styled.div`
  display: flex;
  width: 100%;
  min-height: 48px;
  padding: 0.5rem;
`

export const ContextMenuTitle = styled.div`
  display: flex;
  padding: 1rem 1rem 0.5rem 1rem;
  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-600']]}
  ${textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-R']]}
  color: ${colorsOptions.greys['grey-70']};
  text-transform: uppercase;
`

export const NotFoundContainerBody = styled.tbody`
  background-color: ${colorsOptions.greys['grey-10']};
`

export const NotFoundContainerRow = styled.tr`
  background-color: ${colorsOptions.greys['grey-10']};
`

export const NotFoundContainerCell = styled.td`
  width: 100%;
  height: 460px;
  padding: 1rem 2.5rem 2rem;
  vertical-align: middle;
  border-top: 1px solid ${colorsOptions.greys['grey-20']};
  background-color: ${colorsOptions.greys['grey-10']};
`

export const NotFoundContainerContent = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2rem;
`

// Revision Button

export const RevisionButtonContainer = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  width: 56px;
  border: 0;
  border-radius: 4px;

  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-600']]}
  ${textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-S']]}
  background-color: ${colorsOptions.greys['grey-10']};
  color: ${colorsOptions.greys['grey-100']};

  &:disabled {
    cursor: default;
    background-color: ${colorsOptions.greys.white};
    color: ${colorsOptions.greys['grey-70']};
  }

  &:hover:not(:disabled) {
    cursor: pointer;

    background-color: ${colorsOptions.greys['grey-20']};
    color: ${colorsOptions.greys['grey-100']};
  }

  &:active:not(:disabled) {
    background-color: ${colorsOptions.greys['grey-30']};
    color: ${colorsOptions.greys['grey-100']};
  }

  ${({ isSelected = false }) =>
    isSelected &&
    css`
      background-color: ${colorsOptions.greys['grey-30']};
      color: ${colorsOptions.greys['grey-100']};
    `}
`

export const RevisionListTitle = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-600']]}
  ${textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-R']]}
  color: ${colorsOptions.greys['grey-70']};
  text-transform: uppercase;
  padding: 1rem 1rem 0.5rem;
`

// Plan menu tile view

export const LoadingNextPage = styled.div`
  padding-bottom: 2rem;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const PlanMenuTileListContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  min-height: 300px;
  padding: 0.5rem 0 2.5rem;
`

export const TileBackFolderCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 255px;
  min-height: 300px;
  height: 100%;
  padding: 1rem;
  border-radius: 16px;
  background-color: ${colorsOptions.greys.white};
  border: 1px solid ${colorsOptions.greys['grey-20']};
  flex: 0 0 auto;
  cursor: pointer;
  z-index: 2;

  &:hover {
    background-color: ${colorsOptions.greys['grey-05']};
  }

  &:active {
    background-color: ${colorsOptions.greys['grey-10']};
  }
`

export const TileToolbarContainer = styled.div`
  padding: 0.5rem 0.25rem 0rem 1rem;
`

export const NotFoundContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5rem 2.5rem 2rem;
`

export const EmptyPlanTreeContainer = styled(NotFoundContainer)`
  position: absolute;
  width: 100%;
  z-index: 1;
`

export const EmptyContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 432px;
  gap: 2rem;
`

export const EmptyPlanTreeButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`

// Confirm change revision modal

export const ModalContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 2rem 1rem;
`

export const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

export const DescriptionText = styled.div`
  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-500']]}
  ${textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-R']]}
  color: ${colorsOptions.greys['grey-100']};
`

// Plan menu mobile view

export const PlanMenuMobileViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 0 1rem 4.5rem;
  background-color: ${colorsOptions.greys['grey-10']};
`

export const MobileViewHeaderContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;

  gap: 0.5rem;

  background-color: ${colorsOptions.greys['grey-10']};
`

export const MobileHeaderTitle = styled.div`
  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-700']]}
  ${textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-XL']]};
  text-align: center;

  color: ${colorsOptions.greys.black};

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;

  transition:
    font-size 0.2s ease,
    line-height 0.2s ease;
`

export const MobileHeaderSideContainer = styled.div`
  width: 20%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`

export const SearchContainer = styled.div`
  position: sticky;
  padding: 1rem 0;
  background-color: ${colorsOptions.greys['grey-10']};
  top: 64px;
  z-index: 10;
`

export const PlanDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

export const PlanDescriptionRow = styled.ul`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`

export const PlanDescriptionText = styled.li`
  display: flex;
  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-500']]}
  ${textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-S']]}
  color: ${colorsOptions.greys['grey-70']};

  ${({ activeListStyle = false }) =>
    activeListStyle &&
    css`
      list-style-type: disc;
      list-style-position: inside;
    `}
`

export const CounterLabel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.125rem;
`

export const EmptyFolderLabel = styled.div`
  ${textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-R']]};
  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-500']]};
  color: ${colorsOptions.greys['grey-70']};

  text-transform: lowercase;

  display: flex;
  align-items: center;
  padding: 1rem;
`

export const LoaderChildrenItems = styled.div`
  padding-bottom: 2rem;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const NotFoundContainerMobile = styled(NotFoundContainer)`
  align-items: flex-start;
  padding-top: 3.5rem;
`
